import { createApi } from '@reduxjs/toolkit/query/react';
import { backend_server_address } from '../../../../../backendServer';
import { axiosBaseQuery } from '../../../../../store/utility';

export const dashboardsAPI = createApi({
    reducerPath: 'dashboardsApi',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address + '/api/analytics/datasource/' }),
    tagTypes: ['DashboardData', 'DashboardSchema'],
    endpoints: (builder) => ({
        getDashboardData: builder.query({
            query: (data) => {
                return {
                    url: 'generate/',
                    method: 'POST',
                    withCredentials: true,
                    data: data,
                };
            },
            serializeQueryArgs: ({ queryArgs }) => JSON.stringify(queryArgs),
            // One tag per datasource in the request, so an invalidation refetches only the entries
            // that actually carry the stale datasource. Insights keeps a cache entry per schema tab
            // mounted at once; a coarse tag would refetch all of them (and every datasource in each).
            providesTags: (result, error, body) =>
                body.datasources.map((datasource: string) => ({ type: 'DashboardData' as const, id: datasource })),
        }),
        getEnergyDashboardSchema: builder.query({
            query: (nodeId) => ({
                url: `energy_dashboard_schema/?node=${nodeId}`,
                method: 'GET',
                withCredentials: true,
            }),
            providesTags: (result, error, body) => [{ type: 'DashboardSchema', id: body.id }],
        }),
        getInsightsDashboardSchema: builder.query({
            query: (params) => ({
                url: `insights_dashboard_schema/`,
                method: 'GET',
                withCredentials: true,
                params,
            }),
        }),
        downloadRawData: builder.mutation({
            query: (data) => ({
                url: `export/`,
                method: 'POST',
                withCredentials: true,
                data,
                responseHandler: async (response: any) => {
                    return await response.blob();
                },
            }),
        }),
    }),
});

// The only analytics datasource that aggregates maintenance events: the Insights wins chart sums
// the saved amount and avoided downtime of `Improvement` rows, and counts the ones still pending
// quantification or confirmation for its shortcut buttons. Every other datasource reads
// diagnostics, alarms or devices, so a maintenance mutation leaves them current.
const MAINTENANCE_DRIVEN_DATASOURCES = ['saved_repair_amount'];

/**
 * Drop the cached dashboard payloads that aggregate maintenance events, so the dashboards showing
 * them refetch. Used cross-slice by the maintenance mutations (a win's amounts and its pending
 * state feed the Insights wins chart, which reads `getDashboardData`, not the feed): RTK Query
 * can't express that with `invalidatesTags` across APIs. Only active subscriptions refetch, so it's a no-op outside the
 * Insights / Energy monitoring routes.
 */
export const invalidateMaintenanceDashboardData = () =>
    dashboardsAPI.util.invalidateTags(
        MAINTENANCE_DRIVEN_DATASOURCES.map((datasource) => ({ type: 'DashboardData' as const, id: datasource })),
    );

export const {
    endpoints,
    useGetDashboardDataQuery,
    useGetEnergyDashboardSchemaQuery,
    useGetInsightsDashboardSchemaQuery,
    useDownloadRawDataMutation,
} = dashboardsAPI;
export const dashboardsAPIReducer = dashboardsAPI.reducer;
export const dashboardsAPIReducerPath = dashboardsAPI.reducerPath;
export const dashboardsAPIReducerMiddleware = dashboardsAPI.middleware;
